import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Sparkles, X, Copy, Check, Send, ClipboardList, ListChecks, MessageSquare } from 'lucide-react';
import { MavisApiError, askMeetingReview } from '../../lib/mavisApi';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface Props {
  meetingId: string;
  open: boolean;
  onClose: () => void;
}

const SHORTCUTS = [
  { intent: 'summary', label: 'Give me a summary', icon: Sparkles },
  { intent: 'minutes', label: 'Generate Minutes of the Meeting', icon: ClipboardList },
  { intent: 'actions', label: 'What are my call to actions', icon: ListChecks },
];

const FOCUSABLE = 'button:not(:disabled),textarea:not(:disabled),a[href],input:not(:disabled)';

function askErrorMessage(err: unknown): string {
  if (err instanceof MavisApiError) {
    return typeof err.body?.error === 'string' ? err.body.error : 'Meeting AI could not answer just now.';
  }
  return err instanceof Error ? err.message : 'Meeting AI is unavailable.';
}

function ThinkingIndicator() {
  return (
    <div role="status" aria-live="polite" className="rounded-xl border border-app-line p-4">
      <div className="flex gap-2 items-center text-xs font-medium text-app-muted mb-3">
        <Sparkles className="w-4 h-4 text-app-brand motion-safe:animate-pulse" />
        Reading the conversation…
      </div>
      <div className="space-y-2 motion-safe:animate-pulse">
        <div className="h-2 rounded-full bg-app-line/60 w-5/6" />
        <div className="h-2 rounded-full bg-app-accent/70 w-3/4" />
        <div className="h-2 rounded-full bg-app-line/60 w-1/2" />
      </div>
    </div>
  );
}

export default function MavisMeetingAI({ meetingId, open, onClose }: Props) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [question, setQuestion] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(-1);
  const dialog = useRef<HTMLElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  const answers = useRef<HTMLDivElement>(null);
  const operation = useRef(false);
  const mounted = useRef(true);
  const controller = useRef<AbortController | null>(null);
  const lastFocus = useRef<HTMLElement | null>(null);
  const reduced = useReducedMotion();

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      controller.current?.abort();
    };
  }, []);

  // Focus the question box when opened and restore the previous focus when closed.
  useEffect(() => {
    if (!open) return;
    lastFocus.current = document.activeElement as HTMLElement;
    const timer = setTimeout(() => input.current?.focus(), 0);
    return () => {
      clearTimeout(timer);
      lastFocus.current?.focus();
    };
  }, [open]);

  useEffect(() => {
    answers.current?.scrollTo({ top: answers.current.scrollHeight, behavior: reduced ? 'auto' : 'smooth' });
  }, [messages, busy, open, reduced]);

  const ask = async (intent: string, text: string) => {
    if (operation.current || !text.trim()) return;
    operation.current = true;
    setBusy(true);
    setError('');
    controller.current = new AbortController();
    const history = messages.slice(-6);
    setMessages((m) => [...m, { role: 'user', content: text }]);
    setQuestion('');
    try {
      const data = await askMeetingReview(
        meetingId,
        { intent, question: text, history },
        { signal: controller.current.signal },
      );
      if (mounted.current) setMessages((m) => [...m, { role: 'assistant', content: data.answer }]);
    } catch (err) {
      if (mounted.current) {
        setError(askErrorMessage(err));
        setQuestion(text);
      }
    } finally {
      operation.current = false;
      if (mounted.current) setBusy(false);
    }
  };

  const copy = async (text: string, index: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(index);
      setTimeout(() => {
        if (mounted.current) setCopied(-1);
      }, 1600);
    } catch {
      setError('Could not copy automatically. Select the answer text to copy it.');
    }
  };

  // Escape closes the panel; Tab cycles focus inside the dialog.
  const onDialogKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose();
    }
    if (e.key === 'Tab') {
      const focusable = dialog.current?.querySelectorAll<HTMLElement>(FOCUSABLE);
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  const onQuestionKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void ask('question', question);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/30 backdrop-blur-[2px]"
            aria-label="Close meeting AI"
            onClick={onClose}
          />
          <motion.aside
            ref={dialog}
            initial={{ x: reduced ? 0 : 40, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: reduced ? 0 : 40, opacity: 0 }}
            transition={{ duration: reduced ? 0 : 0.2 }}
            className="relative w-full max-w-lg h-full bg-app-surface border-l border-app-line shadow-2xl flex flex-col"
            role="dialog"
            aria-modal="true"
            aria-label="Ask MAVIS about this meeting"
            onKeyDown={onDialogKeyDown}
          >
            <header className="flex items-center justify-between px-5 py-4 border-b border-app-line">
              <div className="flex gap-3 items-center">
                <span className="w-9 h-9 rounded-xl bg-app-accent flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-app-ink" />
                </span>
                <div>
                  <h2 className="font-semibold text-app-ink">Ask MAVIS</h2>
                  <p className="text-xs text-app-muted mt-0.5">Answers from this meeting</p>
                </div>
              </div>
              <button className="btn-ghost !p-2" aria-label="Close AI panel" onClick={onClose}>
                <X className="w-4 h-4" />
              </button>
            </header>

            <div className="px-5 py-4 border-b border-app-line space-y-2">
              {SHORTCUTS.map(({ intent, label, icon: Icon }) => (
                <button
                  key={intent}
                  className="w-full rounded-xl border border-app-line py-2.5 px-3 text-left text-sm text-app-ink hover:bg-app-canvas transition-colors flex gap-2 items-center disabled:opacity-50"
                  disabled={busy}
                  onClick={() => void ask(intent, label)}
                >
                  <Icon className="w-4 h-4 text-app-muted" />
                  {label}
                </button>
              ))}
            </div>

            <div ref={answers} className="flex-1 min-h-0 overflow-y-auto px-5 py-5 space-y-4" aria-label="Meeting AI conversation">
              {!messages.length && (
                <div className="text-center py-10 text-app-muted">
                  <MessageSquare className="w-7 h-7 mx-auto mb-3" />
                  <p className="text-sm">Ask about what was said, agreed or left open.</p>
                  <p className="text-xs mt-2">Answers stay here while this meeting page is open.</p>
                </div>
              )}
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={m.role === 'user'
                    ? 'ml-8 rounded-xl bg-app-canvas p-3 text-sm text-app-ink'
                    : 'rounded-xl border border-app-line bg-app-surface-raised p-4'}
                >
                  {m.role === 'assistant' && (
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-app-muted">MAVIS</span>
                      <button className="btn-ghost !p-1.5" aria-label={`Copy answer ${i + 1}`} onClick={() => void copy(m.content, i)}>
                        {copied === i ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  )}
                  <p className="whitespace-pre-wrap text-sm leading-7 text-app-ink">{m.content}</p>
                </div>
              ))}
              {busy && <ThinkingIndicator />}
              {error && <p role="alert" className="text-xs text-app-critical rounded-xl bg-app-canvas px-3 py-2">{error}</p>}
            </div>

            <form
              className="p-5 border-t border-app-line"
              onSubmit={(e) => {
                e.preventDefault();
                void ask('question', question);
              }}
            >
              <div className="flex items-end gap-2">
                <textarea
                  ref={input}
                  aria-label="Question about this meeting"
                  className="input-field resize-none !text-sm min-h-20"
                  rows={2}
                  maxLength={2000}
                  placeholder="Ask a question about this meeting"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  onKeyDown={onQuestionKeyDown}
                />
                <button className="btn-primary !p-3 shrink-0" disabled={busy || !question.trim()} aria-label="Send meeting question">
                  <Send className="w-4 h-4" />
                </button>
              </div>
              <p className="text-[11px] text-app-muted mt-2">Based on the captured transcript. Check important details against the recording.</p>
            </form>
          </motion.aside>
        </div>
      )}
    </AnimatePresence>
  );
}
